import { formatFixed, tt } from '@agos/shared'

/**
 * What a franchise is charged on top of its package - `SAAS.md` §3.37.
 *
 * **Eight endpoints and no callers, the two reads included** - so the Tower could not show what a
 * franchise pays, let alone change it. `SAAS-STATUS.md` had carried the line *"their defect was on
 * the server, not the screen"*, which is true and does not stop the screen being missing. Legacy has
 * both sections in full (`artifacts/super-admin/src/pages/FranchiseDetail.tsx`), so this is a missed
 * port rather than a design question.
 *
 * **These rows are money already going out.** `BillingRunner.hardwareLines` and `moduleLines` turn
 * every enabled row into a named line on the monthly invoice. On the owner's data that is
 * `Hardvér: Fiskálna tlačiaren N5` at 25,00 and `Modul: Kupony a zlavy` at 20,00 - **45,00 EUR a
 * month on franchise 1's invoice, set by SQL and visible nowhere**.
 *
 * **Price and cost are not the same question and are validated differently.** `monthly_price` is what
 * the franchise is billed and §10A.130 refuses nought or less on both doors, because a negative price
 * became a negative line on an issued tax document. `monthly_cost` feeds `ProfitabilityService` and is
 * never billed - `Kupony a zlavy` costs the owner nothing to run, so **zero is correct there** and
 * refusing it would make the owner's own row unsaveable.
 */

export interface HardwareItem {
  id: number
  description: string
  monthlyPrice: number
  monthlyCost: number
  enabled: boolean
  /** ISO, or `''` where the row has none - one fewer null for every consumer. */
  startsAt: string
  endsAt: string
}

export interface ModuleChargeItem {
  id: number
  moduleKey: string
  description: string
  monthlyPrice: number
  monthlyCost: number
  enabled: boolean
  /** From the `LEFT JOIN modules`, so it is empty for a key the catalogue no longer holds. */
  moduleName: string
}

export interface CatalogModule {
  key: string
  name: string
  category: string
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** The server answers money as a string off `numeric`, so every reader has to parse it. */
function money(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  const parsed = Number(text(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : 0
}

function rowsOf(payload: unknown, field: string): Record<string, unknown>[] {
  const holder = payload as Record<string, unknown> | undefined
  const rows = holder?.[field]
  return Array.isArray(rows) ? (rows as Record<string, unknown>[]) : []
}

/** `{ hardware: [...] }`, the envelope `FranchiseBillingItemsController` actually sends. */
export function hardwareRowsOf(payload: unknown): HardwareItem[] {
  return rowsOf(payload, 'hardware').flatMap((row) =>
    typeof row.id === 'number'
      ? [
          {
            id: row.id,
            description: text(row.description),
            monthlyPrice: money(row.monthlyPrice),
            monthlyCost: money(row.monthlyCost),
            enabled: row.enabled !== false,
            startsAt: text(row.startsAt),
            endsAt: text(row.endsAt),
          },
        ]
      : [],
  )
}

/** `{ moduleBilling: [...] }` - the field name is `moduleBilling`, not `modules`. */
export function moduleRowsOf(payload: unknown): ModuleChargeItem[] {
  return rowsOf(payload, 'moduleBilling').flatMap((row) =>
    typeof row.id === 'number'
      ? [
          {
            id: row.id,
            moduleKey: text(row.moduleKey),
            description: text(row.description),
            monthlyPrice: money(row.monthlyPrice),
            monthlyCost: money(row.monthlyCost),
            enabled: row.enabled !== false,
            moduleName: text(row.moduleName),
          },
        ]
      : [],
  )
}

/** `{ modules: [...] }` from `GET /super-admin/modules-catalog`. */
export function catalogOf(payload: unknown): CatalogModule[] {
  return rowsOf(payload, 'modules').flatMap((row) =>
    typeof row.key === 'string' && row.key !== ''
      ? [{ key: row.key, name: text(row.name) || row.key, category: text(row.category) }]
      : [],
  )
}

/** What to show for a charged module: its catalogue name, falling back to the raw key. */
export function moduleLabel(item: ModuleChargeItem): string {
  return item.moduleName !== '' ? item.moduleName : item.moduleKey
}

export interface BillingItemDraft {
  description: string
  moduleKey?: string
  monthlyPrice: string
  monthlyCost: string
  enabled: boolean
  /**
   * The rental period - §3.120. Hardware only; `franchise_module_billing` has no date columns.
   *
   * **`yyyy-mm-dd`, because that is what `<input type="date">` holds and what the server takes.**
   * `Instants`' own javadoc says so: *"A date picker sends `2026-07-15`"*. Absent on a module draft,
   * which is why both are optional rather than empty strings.
   */
  startsAt?: string
  /** Blank means *still out*. Clearing one that was set sends an explicit `null` - §10A.233. */
  endsAt?: string
}

export type BillingItemProblem =
  | 'no-description'
  | 'no-module'
  | 'price-not-positive'
  | 'cost-negative'
  | 'no-start'
  | 'ends-before-starts'
  | null

/**
 * What the server would refuse, said before the request - §10A.130.
 *
 * **The price floor is `<= 0` and the cost floor is `< 0`, deliberately.** A price of nought is a
 * line the billing run skips and a row nobody can explain; a **cost** of nought is the owner's real
 * `Kupony a zlavy` row, which costs them nothing to run. Treating the two the same way would make an
 * existing row unsaveable, which is the mistake §3.14's form was built to avoid.
 *
 * §3.25's rule still holds: this is a courtesy so the operator is not shown a server error, never the
 * enforcement. `FranchiseBillingItemsService` refuses on create **and** on patch.
 */
export function billingItemProblem(
  draft: BillingItemDraft,
  kind: 'hardware' | 'module',
): BillingItemProblem {
  if (kind === 'module' && (draft.moduleKey ?? '') === '') return 'no-module'
  // A hardware row is named by its description; a module row is named by its key and may carry a
  // description only as the label on the invoice, which `moduleLines` falls back from.
  if (kind === 'hardware' && draft.description.trim() === '') return 'no-description'

  const price = Number(draft.monthlyPrice.replace(',', '.'))
  if (!Number.isFinite(price) || price <= 0) return 'price-not-positive'

  const cost = Number(draft.monthlyCost.replace(',', '.'))
  if (!Number.isFinite(cost) || cost < 0) return 'cost-negative'

  /* §3.120 - the period, hardware only.
   *
   * **`starts_at` is `NOT NULL DEFAULT now()`**, so a blank start on an existing row would send the
   * null that §10A.233 refuses. `draftOfHardware` seeds a new row with today, so the field is never
   * blank to begin with and this is about somebody emptying it.
   *
   * **A reversed period is refused here and nowhere else.** The server stores it happily and
   * `BillingRunner` then skips the row on every period, which looks exactly like a device that
   * vanished rather than like a date typed the wrong way round. */
  if (kind === 'hardware') {
    const startsAt = (draft.startsAt ?? '').trim()
    if (startsAt === '') return 'no-start'
    const endsAt = (draft.endsAt ?? '').trim()
    // String comparison, and only because both are `yyyy-mm-dd`: that format sorts as it reads, so
    // no parsing and no timezone. A malformed value fails the equality above or reads as a refusal
    // here, which is the safe direction.
    if (endsAt !== '' && endsAt < startsAt) return 'ends-before-starts'
  }
  return null
}

export function billingItemProblemMessage(problem: BillingItemProblem): string {
  switch (problem) {
    case 'no-description':
      return tt('Zadajte popis - objaví sa ako položka na faktúre.')
    case 'no-module':
      return tt('Vyberte modul.')
    case 'price-not-positive':
      return tt('Cena musí byť vyššia než nula - nulová položka sa na faktúru nedostane.')
    case 'cost-negative':
      return tt('Náklad nemôže byť negatívny. Nula je v poriadku.')
    case 'no-start':
      return tt('Zadajte dátum od - zariadenie sa účtuje od dňa, kedy bolo nasadené.')
    case 'ends-before-starts':
      return tt('Dátum do nemôže byť pred dátumom od.')
    default:
      return ''
  }
}

/**
 * The modules a new charge may be added for.
 *
 * **This is load-bearing, not tidiness.** `addModuleBilling` is `ON CONFLICT (franchise_id,
 * module_key) DO UPDATE`, so offering a key that is already charged makes *Pridať* silently
 * **overwrite** an existing row - a charge changed by a button labelled *add*. Legacy filters the
 * catalogue for this reason.
 *
 * **Editing offers everything**, because it is editing a row that already exists and its own key must
 * stay on the list or the form would open with nothing selected.
 */
export function availableModules(
  catalog: readonly CatalogModule[],
  charged: readonly ModuleChargeItem[],
  editing: boolean,
): CatalogModule[] {
  if (editing) return [...catalog]
  const taken = new Set(charged.map((item) => item.moduleKey))
  return catalog.filter((module) => !taken.has(module.key))
}

export interface MonthlyTotals {
  /** What the franchise is billed each month for these rows. */
  chargedCents: number
  /** What they cost the owner - `ProfitabilityService`'s side of it. */
  costCents: number
  marginCents: number
  /** Rows switched off, which are on screen and on no invoice. */
  disabled: number
}

/**
 * The figure neither table showed.
 *
 * Legacy prints a price and a cost per row and never totals either, so *what does this franchise pay
 * us a month* was a sum done by eye. Only **enabled** rows count, because those are the only ones
 * `BillingRunner` turns into lines - and the count of disabled ones is carried so the difference
 * between *nothing is charged* and *nothing is configured* is on screen.
 *
 * In cents, because adding 25,00 and 20,00 in floating point is how a total comes out as 44,99.
 */
export function monthlyTotals(
  hardware: readonly HardwareItem[],
  modules: readonly ModuleChargeItem[],
): MonthlyTotals {
  const rows = [...hardware, ...modules]
  const live = rows.filter((row) => row.enabled)
  const chargedCents = live.reduce((sum, row) => sum + Math.round(row.monthlyPrice * 100), 0)
  const costCents = live.reduce((sum, row) => sum + Math.round(row.monthlyCost * 100), 0)
  return {
    chargedCents,
    costCents,
    marginCents: chargedCents - costCents,
    disabled: rows.length - live.length,
  }
}

/** Only what changed, the §3.5 diff - `updateHardware` builds its `SET` from the keys present. */
export function billingItemPatch(
  draft: BillingItemDraft,
  stored: HardwareItem | ModuleChargeItem,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  const description = draft.description.trim()
  if (description !== stored.description) patch.description = description
  const price = Number(draft.monthlyPrice.replace(',', '.'))
  if (price !== stored.monthlyPrice) patch.monthlyPrice = price
  const cost = Number(draft.monthlyCost.replace(',', '.'))
  if (cost !== stored.monthlyCost) patch.monthlyCost = cost
  if (draft.enabled !== stored.enabled) patch.enabled = draft.enabled

  /* §3.120 - the rental period, hardware only.
   *
   * **Compared against the stored value as a date input holds it**, not against the raw instant: the
   * API sends `2026-07-15T00:00:00Z` and the field holds `2026-07-15`, so comparing the two directly
   * would send the start on every save and write a column nobody touched - the §3.5 rule this whole
   * function exists for.
   *
   * **A cleared end sends an explicit `null`, and that is the one place a key carries a null on
   * purpose.** `Patches.instant` reads absent as *leave it* and null as *clear it*, so omitting it
   * would leave a device recorded as returned for ever. §10A.233 refuses the same null on `startsAt`,
   * where the column is `NOT NULL`. */
  if ('startsAt' in draft || 'endsAt' in draft) {
    const period = stored as Partial<HardwareItem>
    if (typeof period.startsAt === 'string') {
      const startsAt = (draft.startsAt ?? '').trim()
      if (startsAt !== '' && startsAt !== dateInputValue(period.startsAt)) patch.startsAt = startsAt
    }
    if (typeof period.endsAt === 'string') {
      const endsAt = (draft.endsAt ?? '').trim()
      const was = dateInputValue(period.endsAt)
      if (endsAt !== was) patch.endsAt = endsAt === '' ? null : endsAt
    }
  }
  return patch
}

/**
 * The form's starting values, formatted the way a Slovak screen writes money.
 *
 * **`formatFixed`, not `toFixed` and not `formatDecimal`** - and an e2e caught the middle one. Lint
 * rule N-02 refuses `toFixed` because it writes `25.00` where this keyboard types `25,00`; the first
 * version used `formatDecimal`, whose width is a **maximum**, so a price of 25,00 rendered as
 * `25 €` and the spec asserting the owner's real figure failed. `formatFixed`'s own docstring is
 * about exactly this: a maximum *"is right for a percentage and wrong for a quoted price"*. The input
 * is the same field the operator edits, so it is a Slovak screen like any other, and
 * `billingItemProblem` and `billingItemPatch` both read a comma back as a decimal point.
 */
/**
 * An instant from the API as a date input holds it - §3.120.
 *
 * The API sends a full ISO instant and `<input type="date">` takes `yyyy-mm-dd`, so this is the first
 * ten characters **and only when they look like a date**. A row whose `startsAt` did not arrive gives
 * an empty field rather than a nonsense one; `rentalPeriodOf` reports that separately, because a
 * device with no recorded start is a thing an operator should see rather than a thing to guess at.
 */
export function dateInputValue(instant: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(instant) ? instant.slice(0, 10) : ''
}

/** Today as a date input holds it, in the operator's own timezone rather than UTC. */
export function todayInputValue(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

/**
 * @param today seeded into a **new** row's start, so the field is never blank and the operator can
 *     see and change the day the billing run will charge from. A parameter so a test is not at the
 *     mercy of the clock; every call site omits it.
 */
export function draftOfHardware(item: HardwareItem | null, today = todayInputValue()): BillingItemDraft {
  return {
    description: item?.description ?? '',
    monthlyPrice: item ? formatFixed(item.monthlyPrice, 2) : '',
    monthlyCost: item ? formatFixed(item.monthlyCost, 2) : formatFixed(0, 2),
    enabled: item?.enabled ?? true,
    // §3.120 - an existing row's own dates, or today and still-out for a new one.
    startsAt: item ? dateInputValue(item.startsAt) : today,
    endsAt: item ? dateInputValue(item.endsAt) : '',
  }
}

/**
 * What the hardware table says about a rental - §3.120.
 *
 * <p>Separate from the component so the three states can be asserted without matching rendered text,
 * and because *which devices are still out* is half of what this entry is for: an operator who cannot
 * see the period cannot end one.
 */
export type RentalPeriod =
  | { kind: 'out'; since: string }
  | { kind: 'returned'; since: string; returnedOn: string }
  /** No readable start. `starts_at` is `NOT NULL`, so this means the field did not reach the screen. */
  | { kind: 'unknown' }

export function rentalPeriodOf(item: HardwareItem): RentalPeriod {
  const since = dateInputValue(item.startsAt)
  if (since === '') return { kind: 'unknown' }
  const returnedOn = dateInputValue(item.endsAt)
  return returnedOn === ''
    ? { kind: 'out', since }
    : { kind: 'returned', since, returnedOn }
}

export function draftOfModule(
  item: ModuleChargeItem | null,
  firstAvailable: string,
): BillingItemDraft {
  return {
    description: item?.description ?? '',
    moduleKey: item?.moduleKey ?? firstAvailable,
    monthlyPrice: item ? formatFixed(item.monthlyPrice, 2) : '',
    monthlyCost: item ? formatFixed(item.monthlyCost, 2) : formatFixed(0, 2),
    enabled: item?.enabled ?? true,
  }
}
